'use client';

import { cloneElement, FunctionComponent, type Ref } from 'react';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { useInteraction } from '@/react/hooks/useInteraction';
import { isNullOrEmpty } from '@/utils/null-utils';
import { Circle } from '../ButtonDecorations/Circle';
import { Spinner } from '../../Spinner';
import type { IVisualButtonProps } from './IVisualButtonProps';
import type { ButtonSize, ButtonVariant } from '../IBaseButtonProps';

import S from '../styles.base.module.scss';

/**
 * Map the variant to the corresponding style class.
 */
const mapVariantToStyle: Record<ButtonVariant, string> = {
  primary: S.primary,
  secondary: S.secondary,
  tertiary: S.tertiary,
  danger: S.danger,
  link: S.link,
  'decorative-link': S.decorativeLink,
  'hand-drawn-circle': S.handDrawnCircle,
  text: S.text,
  'transparent-light': S.transparentLight,
  'transparent-dark': S.transparentDark,
  'tertiary-light': S.tertiaryLight,
  'outline-light': S.outlineLight
};

/**
 * Get the core styling from the variant string.
 * @param variant - String that represents a button styling type.
 * @returns Returns a className from the styling import.
 */
const getStylingFromVariant = (variant: ButtonVariant): string => {
  return mapVariantToStyle[variant] ?? '';
};

/**
 * Map the size to the corresponding style class.
 */
const mapSizeToStyle: Record<ButtonSize, string> = {
  sm: S.sizeSm,
  md: S.sizeMd,
  lg: S.sizeLg
};

/**
 * Get the styling for size.
 * @param size - String that represents a button size.
 * @returns Returns a className from the styling import.
 */
const getStylingFromSize = (size: ButtonSize): string => {
  return mapSizeToStyle[size] ?? '';
};

/**
 * `VisualButton` is a component that provides consistent button styling
 * and can be used with different underlying elements (button, NextLink, etc.).
 * It handles the visual appearance while delegating the actual element behavior
 * to the `as` prop.
 */
export const VisualButton: FunctionComponent<IVisualButtonProps> = ({
  id,
  actionId,
  className,
  variant = 'primary',
  fullWidth,
  title = '',
  ariaLabel,
  ariaExpanded,
  role,
  size = 'md',
  disabled = false,
  hover = false,
  loading = false,
  onClick,
  onKeyPress,
  style,
  interactionDetails,
  datatestID,
  children,
  as,
  form,
  submit = !isNullOrEmpty(form),
  ref
}) => {
  const { onClickEvent, onKeyPressEvent } = useInteraction(
    onClick,
    onKeyPress,
    interactionDetails
  );

  const visualClassName = classes(
    className,
    getStylingFromVariant(variant),
    getStylingFromSize(size),
    fullWidth ? S.fullWidth : '',
    disabled ? S.disabled : '',
    hover ? S.hover : '',
    loading ? S.loading : ''
  );

  const commonProps = {
    id,
    className: visualClassName,
    style,
    title,
    'aria-label': ariaLabel,
    'aria-expanded': ariaExpanded,
    role,
    'data-actionid': actionId,
    'data-testid': datatestID
  };

  // If no `as` element is provided, render a default button
  if (!as) {
    return (
      <button
        {...commonProps}
        onClick={onClickEvent}
        onKeyPress={onKeyPressEvent}
        disabled={disabled || loading}
        type={submit ? 'submit' : 'button'}
        form={form}
        ref={ref as Ref<HTMLButtonElement> | undefined}
      >
        {loading ? (
          <Spinner className={S.spinner} />
        ) : (
          <>
            {children}
            {variant === 'hand-drawn-circle' && <Circle />}
          </>
        )}
      </button>
    );
  }

  // Clone the provided element and merge props
  return cloneElement(
    as,
    {
      ...commonProps,
      onClick: onClickEvent,
      onKeyPress: onKeyPressEvent,
      disabled: disabled || loading,
      ref
    } as React.HTMLAttributes<HTMLElement>,
    loading ? (
      <Spinner className={S.spinner} />
    ) : (
      <>
        {children}
        {variant === 'hand-drawn-circle' && <Circle />}
      </>
    )
  );
};

export default VisualButton;
